import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { pickCalendarFilters, pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';
import {
	Button,
	Chip,
	Spinner,
	Divider,
	Input,
	Textarea,
	Select,
	SelectItem,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Breadcrumbs,
	BreadcrumbItem,
	addToast
} from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, Check, X, ArrowUpDown } from 'lucide-react';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';
import { unwrap } from '../utils/apiEnvelope.js';
// Rem23 Sub 1 — shared helpers (extracted from this file to
// `utils/discrepancyDisplay.js`) so CheckInScreen inline-edit uses the
// same full-PUT payload builder. Single source of truth.
import {
	normalizePerson as sharedNormalizePerson,
	normalizeAssignToId as sharedNormalizeAssignToId,
	buildSaveDiscrepancyPayload,
	formatDateWithTime
} from '../utils/discrepancyDisplay.js';

// ─── Constants (from legacy calendar.consts.js) ──────────────────────────────

const STATUS_NAMES = {
	new: 'New',
	open: 'Open',
	inprogress: 'In Progress',
	deferred: 'Deferred',
	closed: 'Closed'
};

const STATUS_OPTIONS = [
	{ key: 'new', label: 'New' },
	{ key: 'open', label: 'Open' },
	{ key: 'inprogress', label: 'In Progress' },
	{ key: 'deferred', label: 'Deferred' },
	{ key: 'closed', label: 'Closed' }
];

const STATUS_COLORS = {
	new: 'secondary',
	open: 'primary',
	inprogress: 'warning',
	deferred: 'default',
	closed: 'success'
};

const PRIORITY_NAMES = { 1: 'High', 2: 'Medium', 3: 'Low' };
const PRIORITY_COLORS = { 1: 'danger', 2: 'warning', 3: 'success' };

const PRIORITY_OPTIONS = [
	{ key: '1', label: 'High' },
	{ key: '2', label: 'Medium' },
	{ key: '3', label: 'Low' }
];

const ACTIVITY_NAMES = {
	add: 'Added a Discrepancy',
	save: 'Changed Discrepancy',
	changeStatus: 'Changed Discrepancy Status',
	comment: 'Added a Comment'
};

const DISCREPANCY_FIELDS = {
	name: 'Name',
	assignTo: 'Assign To',
	status: 'Status',
	priority: 'Priority',
	description: 'Description',
	logbookEntry: 'Logbook Entry'
};

// Rem23 Sub 1 — `normalizePerson`, `normalizeAssignToId`, and the full-PUT
// payload builder now live in `utils/discrepancyDisplay.js` (shared with
// CheckInScreen). Local aliases preserve the existing call sites without a
// component-wide rename.
const normalizePerson = sharedNormalizePerson;
const normalizeAssignToId = sharedNormalizeAssignToId;
const buildSavePayload = buildSaveDiscrepancyPayload;

// ─── InlineField label/value row ─────────────────────────────────────────────

function InlineField({ label, children }) {
	return (
		<div className="mb-4">
			<p className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">{label}</p>
			{children}
		</div>
	);
}

// ─── ActivityRow sub-component ───────────────────────────────────────────────

function ActivityRow({
	activity,
	userSettings,
	timezone,
	canComment,
	currentUserId,
	editingCommentId,
	editingCommentText,
	onEditCommentOpen,
	onEditCommentSave,
	onEditCommentCancel,
	onEditCommentTextChange,
	saving
}) {
	// Prefer nested User object, fall back to flat fields (legacy shape)
	const authorPerson = normalizePerson(activity.author);
	const authorFull =
		authorPerson?.fullName ||
		[activity.authorName, activity.authorSurname].filter(Boolean).join(' ') ||
		'—';

	const isEditing = editingCommentId === activity.id;

	// Gate: edit pencil only for own comments
	const canEditThisComment =
		canComment &&
		((authorPerson?.id != null && String(authorPerson.id) === String(currentUserId)) ||
			(activity.authorId != null && String(activity.authorId) === String(currentUserId)));

	return (
		<div className="text-sm border-l-2 border-default-200 pl-3 py-1">
			<p className="font-medium text-default-700">
				<span>{authorFull}</span>
				{' — '}
				<span className="text-default-500">
					{ACTIVITY_NAMES[activity.action] || activity.action}
				</span>
				<span className="text-xs text-default-400 ml-2">
					{formatDateWithTime(activity.date, userSettings, timezone)}
				</span>
			</p>

			{/* save — show snapshot fields that changed */}
			{activity.action === 'save' && (
				<div className="mt-1 text-xs text-default-600 space-y-0.5">
					{activity.name != null && (
						<div>
							<span className="font-medium">Name:</span> {activity.name}
						</div>
					)}
					{activity.description != null && (
						<div>
							<span className="font-medium">Description:</span> {activity.description}
						</div>
					)}
					{activity.priority != null && (
						<div>
							<span className="font-medium">Priority:</span>{' '}
							{PRIORITY_NAMES[Number(activity.priority)] || String(activity.priority)}
						</div>
					)}
					{activity.assignTo != null && (
						<div>
							<span className="font-medium">Assigned To:</span>{' '}
							{normalizePerson(activity.assignTo)?.fullName || String(activity.assignTo)}
						</div>
					)}
					{activity.logbookEntry != null && (
						<div>
							<span className="font-medium">Logbook Entry:</span> {activity.logbookEntry}
						</div>
					)}
				</div>
			)}

			{/* changeStatus — show new status */}
			{activity.action === 'changeStatus' && (
				<div className="mt-1 text-xs text-default-600">
					<span className="font-medium">Status:</span>{' '}
					{STATUS_NAMES[activity.status] || activity.status || '—'}
				</div>
			)}

			{/* comment — disposition text + own-comment edit */}
			{activity.action === 'comment' && (
				<div className="mt-1">
					{isEditing ? (
						<div className="space-y-2">
							<Textarea
								size="sm"
								minRows={2}
								autoFocus
								value={editingCommentText}
								onValueChange={onEditCommentTextChange}
							/>
							<div className="flex gap-2">
								<Button
									size="sm"
									color="success"
									isDisabled={!editingCommentText.trim()}
									isLoading={saving}
									onPress={onEditCommentSave}
								>
									Save
								</Button>
								<Button size="sm" variant="ghost" onPress={onEditCommentCancel}>
									Cancel
								</Button>
							</div>
						</div>
					) : (
						<div className="flex items-start gap-2">
							<span className="text-default-600 whitespace-pre-wrap">{activity.disposition}</span>
							{canEditThisComment && (
								<button
									onClick={() => onEditCommentOpen(activity.id)}
									className="flex-shrink-0 mt-0.5"
								>
									<Pencil className="w-3 h-3 text-default-400 hover:text-default-600" />
								</button>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

// ─── Main DiscrepancyView component ──────────────────────────────────────────

export default function DiscrepancyView({
	discrepancy: initialDiscrepancy,
	orgUnique,
	currentUserId,
	perms,
	userSettings,
	eventTypeLabel,
	resourceLabel,
	onBack,
	onBackToEvent,
	onDelete
}) {
	const [discr, setDiscr] = useState(initialDiscrepancy);
	const [loading, setLoading] = useState(true);
	const [mechanics, setMechanics] = useState([]);
	const [editingField, setEditingField] = useState(null);
	const [localValue, setLocalValue] = useState('');
	const [activitiesSort, setActivitiesSort] = useState('desc');
	const [showAll, setShowAll] = useState(false);
	const [commentOpen, setCommentOpen] = useState(false);
	const [commentText, setCommentText] = useState('');
	const [editingCommentId, setEditingCommentId] = useState(null);
	const [editingCommentText, setEditingCommentText] = useState('');
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const [deleteReason, setDeleteReason] = useState('');
	const [saving, setSaving] = useState(false);

	// Display tz for discrepancy dates/times (same selector EventViewModal uses).
	const timezone = useSelector(
		state => pickCalendarFilters(state)?.timezone || pickCalendarBootstrap(state)?.timezone || 'UTC'
	);

	// Fetch full discrepancy (with activities) on mount
	useEffect(() => {
		if (!initialDiscrepancy?.id) return;
		let cancelled = false;
		setLoading(true);
		DiscrepanciesApi.getDiscrepancy(initialDiscrepancy.id, orgUnique)
			.then(res => {
				if (cancelled) return;
				const data = unwrap(res);
				if (data) setDiscr(data);
			})
			.catch(() => {
				// Fallback: keep initial partial data, don't block
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [initialDiscrepancy?.id, orgUnique]);

	// Fetch mechanics for AssignTo dropdown
	useEffect(() => {
		DiscrepanciesApi.getMechanics(orgUnique)
			.then(res => {
				const list = unwrap(res);
				setMechanics(Array.isArray(list) ? list : []);
			})
			.catch(() => {
				setMechanics([]);
			});
	}, [orgUnique]);

	const refetch = useCallback(() => {
		if (!discr?.id) return Promise.resolve();
		return DiscrepanciesApi.getDiscrepancy(discr.id, orgUnique)
			.then(res => {
				const d = unwrap(res);
				if (d) setDiscr(d);
			})
			.catch(() => {});
	}, [discr?.id, orgUnique]);

	const openEdit = useCallback(
		field => {
			setEditingField(field);
			if (field === 'assignTo') {
				// assignTo may be a nested User object { id, name, surname }
				const id = normalizeAssignToId(discr?.assignTo);
				setLocalValue(id != null ? String(id) : '');
			} else {
				setLocalValue(String(discr?.[field] ?? ''));
			}
		},
		[discr]
	);

	const cancelEdit = useCallback(() => {
		setEditingField(null);
		setLocalValue('');
	}, []);

	const saveField = useCallback(
		async (field, value) => {
			if (!discr?.id) return;
			const prev = discr[field];
			setDiscr(d => ({ ...d, [field]: value }));
			setEditingField(null);
			setSaving(true);
			try {
				if (field === 'status') {
					// Status uses PATCH — separate endpoint, NOT PUT
					await DiscrepanciesApi.changeStatus(discr.id, { status: value }, orgUnique);
				} else {
					// All other edits use PUT with FULL payload (SaveDiscrepancyRequestDto requires all fields)
					const payload = buildSavePayload(discr, { [field]: value });
					await DiscrepanciesApi.updateDiscrepancy(discr.id, payload, orgUnique);
				}
				await refetch();
				addToast({ title: 'Saved', color: 'success' });
			} catch (err) {
				setDiscr(d => ({ ...d, [field]: prev }));
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
				addToast({ title: 'Save failed', description: msg, color: 'danger' });
			} finally {
				setSaving(false);
			}
		},
		[discr, orgUnique, refetch]
	);

	const handleAddComment = useCallback(async () => {
		if (!commentText.trim() || !discr?.id) return;
		setSaving(true);
		try {
			await DiscrepanciesApi.addComment(discr.id, { disposition: commentText }, orgUnique);
			setCommentOpen(false);
			setCommentText('');
			await refetch();
			addToast({ title: 'Comment added', color: 'success' });
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Failed to add comment';
			addToast({ title: 'Error', description: msg, color: 'danger' });
		} finally {
			setSaving(false);
		}
	}, [commentText, discr?.id, orgUnique, refetch]);

	const handleEditComment = useCallback(
		async activityId => {
			if (!editingCommentText.trim()) return;
			setSaving(true);
			try {
				await DiscrepanciesApi.editComment(
					activityId,
					{ disposition: editingCommentText },
					orgUnique
				);
				setEditingCommentId(null);
				setEditingCommentText('');
				await refetch();
				addToast({ title: 'Comment updated', color: 'success' });
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Failed to update comment';
				addToast({ title: 'Error', description: msg, color: 'danger' });
			} finally {
				setSaving(false);
			}
		},
		[editingCommentText, orgUnique, refetch]
	);

	const handleDelete = useCallback(async () => {
		if (!discr?.id) return;
		setSaving(true);
		try {
			await DiscrepanciesApi.deleteDiscrepancy(discr.id, deleteReason || null, orgUnique);
			addToast({ title: 'Discrepancy deleted', color: 'success' });
			setDeleteConfirm(false);
			onDelete?.();
			onBack?.();
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Delete failed';
			addToast({ title: 'Delete failed', description: msg, color: 'danger' });
		} finally {
			setSaving(false);
		}
	}, [discr?.id, deleteReason, orgUnique, onDelete, onBack]);

	// Activities list
	const rawActivities = Array.isArray(discr?.activities) ? discr.activities : [];
	const sorted = activitiesSort === 'asc' ? rawActivities : [...rawActivities].reverse();
	const hiddenCount = sorted.length > 3 ? sorted.length - 3 : 0;
	const visible = showAll || sorted.length <= 3 ? sorted : sorted.slice(0, 3);

	// Mechanics helpers for AssignTo
	const mechanicsOptions = mechanics.map(m => ({
		key: String(m.id),
		label: `${m.name || ''} ${m.surname || ''}`.trim() || String(m.id)
	}));

	// Permission flags
	const canManage = !!perms?.canManageDiscrepancies;
	const canChangeStatus = !!perms?.canChangeDiscrepancyStatus;
	const canComment = !!perms?.canCommentDiscrepancy;
	const canDel = !!perms?.canDeleteDiscrepancy;
	const isClosed = discr?.status === 'closed';

	return (
		<>
			{/* Breadcrumb header */}
			<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
				<Breadcrumbs>
					<BreadcrumbItem onPress={onBackToEvent}>{eventTypeLabel}</BreadcrumbItem>
					<BreadcrumbItem onPress={onBack}>{resourceLabel || 'Resource'}</BreadcrumbItem>
					<BreadcrumbItem isCurrent>{discr?.name || 'Discrepancy'}</BreadcrumbItem>
				</Breadcrumbs>
			</ModalHeader>

			<ModalBody className="pt-5">
				{loading && (
					<div className="flex justify-center py-4">
						<Spinner size="sm" />
					</div>
				)}

				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2 }}
				>
					{/* Fields grid */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
						{/* Left column */}
						<div>
							{/* Name */}
							<InlineField label="Name">
								{editingField === 'name' ? (
									<div className="flex items-center gap-2">
										<Input
											size="sm"
											value={localValue}
											onValueChange={setLocalValue}
											maxLength={100}
											autoFocus
											className="flex-1"
										/>
										<Button
											isIconOnly
											size="sm"
											color="success"
											isDisabled={!localValue.trim()}
											onPress={() => saveField('name', localValue)}
										>
											<Check className="w-3.5 h-3.5" />
										</Button>
										<Button isIconOnly size="sm" variant="ghost" onPress={cancelEdit}>
											<X className="w-3.5 h-3.5" />
										</Button>
									</div>
								) : (
									<div
										className={`text-sm text-default-900 flex items-center gap-2 ${canManage ? 'cursor-pointer group' : ''}`}
										onClick={canManage ? () => openEdit('name') : undefined}
									>
										<span>{discr?.name || '—'}</span>
										{canManage && (
											<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-100 transition-opacity" />
										)}
									</div>
								)}
							</InlineField>

							{/* Assigned To */}
							<InlineField label="Assigned To">
								{editingField === 'assignTo' && canManage ? (
									<div className="flex items-center gap-2">
										<Select
											size="sm"
											className="flex-1"
											selectedKeys={localValue ? [localValue] : []}
											onSelectionChange={keys => {
												const v = [...keys][0];
												if (v != null) saveField('assignTo', String(v));
											}}
											placeholder="Select mechanic"
										>
											{mechanicsOptions.map(o => (
												<SelectItem key={o.key}>{o.label}</SelectItem>
											))}
										</Select>
										<Button isIconOnly size="sm" variant="ghost" onPress={cancelEdit}>
											<X className="w-3.5 h-3.5" />
										</Button>
									</div>
								) : (
									<div
										className={`text-sm text-default-900 flex items-center gap-2 ${canManage ? 'cursor-pointer group' : ''}`}
										onClick={canManage ? () => openEdit('assignTo') : undefined}
									>
										<span>
											{normalizePerson(discr?.assignTo)?.fullName ||
												`${discr?.assignedName || ''} ${discr?.assignedSurname || ''}`.trim() ||
												'None'}
										</span>
										{canManage && (
											<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-100 transition-opacity" />
										)}
									</div>
								)}
							</InlineField>

							{/* Report By */}
							<InlineField label="Report By">
								<span className="text-sm text-default-900">
									{normalizePerson(discr?.author)?.fullName ||
										[discr?.authorName, discr?.authorSurname].filter(Boolean).join(' ') ||
										'—'}
								</span>
							</InlineField>

							{/* Report Date */}
							<InlineField label="Report Date">
								<span className="text-sm text-default-900">
									{formatDateWithTime(discr?.date, userSettings, timezone)}
								</span>
							</InlineField>

							{/* Logbook Entry — only when status=closed */}
							{isClosed && (
								<InlineField label="Logbook Entry">
									{editingField === 'logbookEntry' && canManage ? (
										<div className="flex flex-col gap-2">
											<Textarea
												size="sm"
												minRows={3}
												autoFocus
												value={localValue}
												onValueChange={setLocalValue}
											/>
											<div className="flex gap-2">
												<Button
													size="sm"
													color="success"
													onPress={() => saveField('logbookEntry', localValue)}
												>
													Save
												</Button>
												<Button size="sm" variant="ghost" onPress={cancelEdit}>
													Cancel
												</Button>
											</div>
										</div>
									) : (
										<div
											className={`text-sm text-default-900 flex items-start gap-2 ${canManage ? 'cursor-pointer group' : ''}`}
											onClick={canManage ? () => openEdit('logbookEntry') : undefined}
										>
											<span className="whitespace-pre-wrap">{discr?.logbookEntry || 'None'}</span>
											{canManage && (
												<Pencil className="w-3 h-3 text-default-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
											)}
										</div>
									)}
								</InlineField>
							)}
						</div>

						{/* Right column */}
						<div>
							{/* Status */}
							<InlineField label="Status">
								{editingField === 'status' && canChangeStatus ? (
									<div className="flex items-center gap-2">
										<Select
											size="sm"
											className="flex-1"
											selectedKeys={localValue ? [localValue] : []}
											onSelectionChange={keys => {
												const v = [...keys][0];
												if (v != null) saveField('status', String(v));
											}}
										>
											{STATUS_OPTIONS.map(o => (
												<SelectItem key={o.key}>{o.label}</SelectItem>
											))}
										</Select>
										<Button isIconOnly size="sm" variant="ghost" onPress={cancelEdit}>
											<X className="w-3.5 h-3.5" />
										</Button>
									</div>
								) : (
									<div
										className={`flex items-center gap-2 ${canChangeStatus ? 'cursor-pointer group' : ''}`}
										onClick={canChangeStatus ? () => openEdit('status') : undefined}
									>
										<Chip
											size="sm"
											variant="flat"
											color={STATUS_COLORS[discr?.status] || 'default'}
										>
											{STATUS_NAMES[discr?.status] || discr?.status || '—'}
										</Chip>
										{canChangeStatus && (
											<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-100 transition-opacity" />
										)}
									</div>
								)}
							</InlineField>

							{/* Priority */}
							<InlineField label="Priority">
								{editingField === 'priority' && canManage ? (
									<div className="flex items-center gap-2">
										<Select
											size="sm"
											className="flex-1"
											selectedKeys={localValue ? [String(localValue)] : []}
											onSelectionChange={keys => {
												const v = [...keys][0];
												if (v != null) saveField('priority', String(v));
											}}
										>
											{PRIORITY_OPTIONS.map(o => (
												<SelectItem key={o.key}>{o.label}</SelectItem>
											))}
										</Select>
										<Button isIconOnly size="sm" variant="ghost" onPress={cancelEdit}>
											<X className="w-3.5 h-3.5" />
										</Button>
									</div>
								) : (
									<div
										className={`flex items-center gap-2 ${canManage ? 'cursor-pointer group' : ''}`}
										onClick={canManage ? () => openEdit('priority') : undefined}
									>
										<Chip
											size="sm"
											variant="flat"
											color={PRIORITY_COLORS[Number(discr?.priority)] || 'default'}
										>
											{PRIORITY_NAMES[Number(discr?.priority)] || discr?.priority || '—'}
										</Chip>
										{canManage && (
											<Pencil className="w-3 h-3 text-default-400 opacity-0 group-hover:opacity-100 transition-opacity" />
										)}
									</div>
								)}
							</InlineField>

							{/* Description */}
							<InlineField label="Description">
								{editingField === 'description' && canManage ? (
									<div className="flex flex-col gap-2">
										<Textarea
											size="sm"
											minRows={3}
											autoFocus
											value={localValue}
											onValueChange={setLocalValue}
										/>
										<div className="flex gap-2">
											<Button
												size="sm"
												color="success"
												onPress={() => saveField('description', localValue)}
											>
												Save
											</Button>
											<Button size="sm" variant="ghost" onPress={cancelEdit}>
												Cancel
											</Button>
										</div>
									</div>
								) : (
									<div
										className={`text-sm text-default-900 flex items-start gap-2 ${canManage ? 'cursor-pointer group' : ''}`}
										onClick={canManage ? () => openEdit('description') : undefined}
									>
										<span className="whitespace-pre-wrap">{discr?.description || 'None'}</span>
										{canManage && (
											<Pencil className="w-3 h-3 text-default-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
										)}
									</div>
								)}
							</InlineField>
						</div>
					</div>

					<Divider className="my-4" />

					{/* Activities History */}
					<div>
						<div className="flex items-center justify-between mb-3">
							<h4 className="text-sm font-semibold text-default-700">History</h4>
							<button
								onClick={() => setActivitiesSort(s => (s === 'asc' ? 'desc' : 'asc'))}
								className="flex items-center gap-1 text-xs text-default-500 hover:text-default-700 transition-colors"
							>
								<ArrowUpDown className="w-3.5 h-3.5" />
								{activitiesSort === 'asc' ? 'Oldest first' : 'Newest first'}
							</button>
						</div>

						{rawActivities.length === 0 ? (
							<p className="text-sm text-default-400 text-center py-4">No activities defined</p>
						) : (
							<div className="space-y-3">
								{!showAll && hiddenCount > 0 && (
									<button
										onClick={() => setShowAll(true)}
										className="w-full text-center text-xs text-default-500 hover:text-default-700 py-2 border border-default-200 rounded-lg transition-colors"
									>
										Show All (+{hiddenCount})
									</button>
								)}
								{visible.map(activity => (
									<ActivityRow
										key={activity.id}
										activity={activity}
										userSettings={userSettings}
										timezone={timezone}
										canComment={canComment}
										currentUserId={currentUserId}
										editingCommentId={editingCommentId}
										editingCommentText={editingCommentText}
										onEditCommentOpen={id => {
											setEditingCommentId(id);
											const act = rawActivities.find(a => a.id === id);
											setEditingCommentText(act?.disposition || '');
										}}
										onEditCommentSave={() => handleEditComment(activity.id)}
										onEditCommentCancel={() => {
											setEditingCommentId(null);
											setEditingCommentText('');
										}}
										onEditCommentTextChange={setEditingCommentText}
										saving={saving}
									/>
								))}
								{showAll && hiddenCount > 0 && (
									<button
										onClick={() => setShowAll(false)}
										className="w-full text-center text-xs text-default-500 hover:text-default-700 py-2 border border-default-200 rounded-lg transition-colors"
									>
										Hide
									</button>
								)}
							</div>
						)}
					</div>

					{/* Add comment */}
					{canComment && (
						<div className="mt-4">
							<AnimatePresence>
								{commentOpen ? (
									<motion.div
										key="comment-form"
										initial={{ opacity: 0, height: 0 }}
										animate={{ opacity: 1, height: 'auto' }}
										exit={{ opacity: 0, height: 0 }}
										className="space-y-2 overflow-hidden"
									>
										<Textarea
											label="Comment"
											size="sm"
											minRows={3}
											value={commentText}
											onValueChange={setCommentText}
											autoFocus
										/>
										<div className="flex gap-2">
											<Button
												size="sm"
												color="primary"
												isDisabled={!commentText.trim()}
												isLoading={saving}
												onPress={handleAddComment}
											>
												Add
											</Button>
											<Button
												size="sm"
												variant="ghost"
												onPress={() => {
													setCommentOpen(false);
													setCommentText('');
												}}
											>
												Cancel
											</Button>
										</div>
									</motion.div>
								) : (
									<Button size="sm" variant="bordered" onPress={() => setCommentOpen(true)}>
										Comment
									</Button>
								)}
							</AnimatePresence>
						</div>
					)}
				</motion.div>
			</ModalBody>

			{canDel && (
				<ModalFooter className="border-t border-default-200 pt-3 justify-end">
					<Button size="sm" color="danger" variant="flat" onPress={() => setDeleteConfirm(true)}>
						Delete Discrepancy
					</Button>
				</ModalFooter>
			)}

			{/* Delete confirm dialog — stacked on top of parent Modal */}
			<Modal
				isOpen={deleteConfirm}
				onClose={() => setDeleteConfirm(false)}
				size="sm"
				placement="center"
			>
				<ModalContent>
					<ModalHeader>Delete Discrepancy?</ModalHeader>
					<ModalBody>
						<p className="text-sm text-default-600 mb-3">
							This discrepancy will be permanently removed.
						</p>
						<Textarea
							label="Reason (optional)"
							size="sm"
							minRows={2}
							value={deleteReason}
							onValueChange={setDeleteReason}
							placeholder="Enter a reason..."
						/>
					</ModalBody>
					<ModalFooter>
						<Button variant="light" onPress={() => setDeleteConfirm(false)}>
							Cancel
						</Button>
						<Button color="danger" isLoading={saving} onPress={handleDelete}>
							Delete
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}
